/**
 * CustomCaptcha.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 * Updated: Aug 2026 — localized messages
 */

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './CustomCaptcha.css';

const CustomCaptcha = ({ onVerify }) => {
  const { t, language } = useLanguage();
  const [isVerified, setIsVerified] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [message, setMessage] = useState(t.auth.captchaHold);

  const holdTimeoutRef = useRef(null);

  // Keep the idle prompt in the active language when the user switches mid-form
  useEffect(() => {
    if (!isVerified && !isHolding) {
      setMessage(t.auth.captchaHold);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to language switches
  }, [language]);

  // Handler when user presses down
  const handleMouseDown = () => {
    if (isVerified || isHolding) return;

    setIsHolding(true);
    setMessage(t.auth.captchaHolding);

    // Start the timer to verify
    holdTimeoutRef.current = setTimeout(() => {
      setIsVerified(true);
      setIsHolding(false);
      setMessage(t.auth.captchaSuccess);
      if (onVerify) {
        onVerify(true);
      }
    }, 1500); // 1.5 seconds
  };

  // Handler when user releases click or moves mouse away
  const handleMouseUpOrLeave = () => {
    if (isVerified || !isHolding) return;

    // Clear the timer if they let go too early
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }

    setIsHolding(false);
    setMessage(t.auth.captchaIncomplete);
  };

  return (
    <div className="custom-captcha-container">
      <p className={`captcha-message ${isVerified ? 'verified' : ''}`}>
        {message}
      </p>

      {/* "I am not a robot" Hold Button */}
      {!isVerified && (
        <div
          className={`captcha-checkbox-area ${isHolding ? 'holding' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={handleMouseUpOrLeave}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUpOrLeave}
          onContextMenu={(e) => e.preventDefault()}
          role="button"
          tabIndex="0"
          aria-label={t.auth.captchaHold}
        >
          <div className="captcha-progress-bar" style={{
            transform: isHolding ? 'translateX(0)' : 'translateX(-100%)',
          }}></div>
          <div className="captcha-label">
            <span className="captcha-icon">🤖</span>
            <span>{t.auth.captchaHoldButton}</span>
          </div>
        </div>
      )}

      {isVerified && (
        <div className="captcha-verified">
          <span className="captcha-checkmark">✅</span>
          <span>{t.auth.captchaVerified}</span>
        </div>
      )}
    </div>
  );
};

export default CustomCaptcha;
